import React, { ReactNode } from 'react';
import styles from './DataTable.module.scss';
import SingleLineSkeleton from '../skeleton-loader/SingleLineSkeletonLoader';

export type ColumnType<T> = {
  header: string;
  accessor: string; 
  render?: (record: T) => ReactNode;
  width?: string | number;
};

export interface DataTableProps<T> {
  /**
   * Data array to be displayed in the table
   */
  data: T[];
  
  /**
   * Column configuration for table
   */
  columns: ColumnType<T>[];
  
  /**
   * Optional loading state
   */
  loading?: boolean;
  isUpdating?: boolean;
  
  /**
   * Optional empty state component
   */
  emptyComponent?: ReactNode;
  
  /**
   * Optional header actions (buttons, filters, etc)
   */
  headerActions?: ReactNode;
  
  /**
   * Optional row click handler
   */
  onRowClick?: (record: T, index: number) => void;
  
  /**
   * Optional className for styling
   */
  className?: string;
}


const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((prev, curr) => {
    return prev && prev[curr] !== undefined ? prev[curr] : undefined;
  }, obj);
};

/**
 * DataTable is a reusable component for displaying tabular data
 * with configurable columns and cell rendering
 */
function DataTable<T extends { id?: string | number }>({
  data,
  columns,
  loading = false,
  isUpdating = false,
  emptyComponent,
  headerActions,
  onRowClick,
  className = '',
}: Readonly<DataTableProps<T>>) {
  const renderCell = (item: T, column: ColumnType<T>) => {
    if (column.render) {
      return column.render(item);
    }
    
    const value = getNestedValue(item, column.accessor);
    
    if (value === null || value === undefined) {
      return '';
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    
    return String(value);
  };

  return (
    <div className={`${styles.tableContainer} ${className}`}>
      {headerActions && (
        <div className={styles.topSection}>
          {headerActions}
        </div>
      )}
      
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={`${index}-${column.header}`}
                  style={{ width: column.width }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
           
          <tbody className={styles.tableBody}>
            <tr></tr>
            {loading && (
              <tr>
                <td colSpan={columns.length} className={styles.loadingState}>
                  Loading...
                </td>
              </tr>
            )}
            
            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={columns.length} className={styles.emptyState}>
                  {emptyComponent || "No data available"}
                </td>
              </tr>
            )}
            
            {!loading && data.length > 0 && data.map((record, index) => (
              <tr
                key={record.id ?? index}
                onClick={onRowClick ? () => onRowClick(record, index) : undefined}
                className={onRowClick ? styles.clickable : ''}
              >
                {columns.map((column) => (
                  <td key={`${record.id ?? index}-${column.header}`}>
                    {renderCell(record, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DataTable;
